// IMG-4: runtime bitmap cache — decoded source, mip levels, and rectified variants.
// Never persisted; the loader (injected by the composition root) reads blobs from IDB.
// Mips use createImageBitmap's native async resize (see DECISIONS.md re: mipWorker).
import { applyHomography, invertHomography } from "../core/homography";
import type { Vec } from "../core/geometry";

export interface CachedImage {
  levels: ImageBitmap[]; // levels[0] = full size, halved down to ≤ 1024 px
  w: number;
  h: number;
}

const MIN_MIP = 1024;
const WARP_GRID = 32; // IMG-5 CPU mesh subdivision

export class AssetCache {
  private images = new Map<string, CachedImage>();
  private pending = new Set<string>();
  private failed = new Set<string>();
  private rectified = new Map<string, CachedImage>();

  /** Injected by main: fetch blob for hash (IDB) — resolves null when missing (EC-EXP-5). */
  loader: ((hash: string) => Promise<Blob | null>) | null = null;
  /** Invalidate the bg layer when a bitmap arrives. */
  onLoaded: (() => void) | null = null;

  get(hash: string): CachedImage | undefined {
    const hit = this.images.get(hash);
    if (!hit && !this.pending.has(hash) && !this.failed.has(hash)) void this.load(hash);
    return hit;
  }

  /** Awaitable variant for export paths: resolves once decoded, null when missing. */
  async ensure(hash: string): Promise<CachedImage | null> {
    for (let i = 0; i < 200; i++) {
      const hit = this.images.get(hash);
      if (hit) return hit;
      if (this.failed.has(hash)) return null;
      if (!this.pending.has(hash)) void this.load(hash);
      await new Promise((r) => setTimeout(r, 25));
    }
    return null;
  }

  isMissing(hash: string): boolean {
    return this.failed.has(hash);
  }

  async set(hash: string, bitmap: ImageBitmap): Promise<void> {
    this.images.set(hash, await buildMips(bitmap));
    this.failed.delete(hash);
    this.onLoaded?.();
  }

  private async load(hash: string): Promise<void> {
    if (!this.loader) return;
    this.pending.add(hash);
    try {
      const blob = await this.loader(hash);
      if (!blob) {
        this.failed.add(hash); // renders as the gray placeholder (EC-EXP-5)
        return;
      }
      const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
      await this.set(hash, bitmap);
    } catch {
      this.failed.add(hash);
    } finally {
      this.pending.delete(hash);
      this.onLoaded?.();
    }
  }

  /** Smallest mip whose scale still covers `pixelsNeeded` of the natural width (IMG-4). */
  pick(img: CachedImage, pixelsNeeded: number): ImageBitmap {
    for (let i = img.levels.length - 1; i >= 0; i--) {
      if (img.levels[i]!.width >= pixelsNeeded) return img.levels[i]!;
    }
    return img.levels[0]!;
  }

  /**
   * IMG-5 CPU path: rectified bitmap for (hash, homography). H maps source px → rectified px.
   * Computed once per (hash, H) and cached; original bytes untouched (SCP-3 L3).
   */
  getRectified(hash: string, h: number[], rw: number, rh: number): CachedImage | undefined {
    const key = `${hash}:${h.map((v) => v.toFixed(6)).join(",")}:${rw}x${rh}`;
    const hit = this.rectified.get(key);
    if (hit) return hit;
    const source = this.get(hash);
    if (!source) return undefined;
    const warped = warpBitmap(source.levels[0]!, h, Math.round(rw), Math.round(rh));
    if (!warped) return undefined;
    void buildMipsFromCanvas(warped).then((cached) => {
      this.rectified.set(key, cached);
      this.onLoaded?.();
    });
    // synchronous first paint: single-level placeholder while mips build
    const immediate: CachedImage = {
      levels: [warped as unknown as ImageBitmap],
      w: warped.width,
      h: warped.height
    };
    this.rectified.set(key, immediate);
    return immediate;
  }

  dropRectified(hash: string): void {
    for (const key of this.rectified.keys()) {
      if (key.startsWith(`${hash}:`)) this.rectified.delete(key);
    }
  }
}

async function buildMips(bitmap: ImageBitmap): Promise<CachedImage> {
  const levels: ImageBitmap[] = [bitmap];
  let w = bitmap.width;
  let h = bitmap.height;
  while (Math.max(w, h) / 2 >= MIN_MIP) {
    w = Math.round(w / 2);
    h = Math.round(h / 2);
    levels.push(
      await createImageBitmap(levels[levels.length - 1]!, {
        resizeWidth: w,
        resizeHeight: h,
        resizeQuality: "high"
      })
    );
  }
  return { levels, w: bitmap.width, h: bitmap.height };
}

async function buildMipsFromCanvas(canvas: HTMLCanvasElement | OffscreenCanvas): Promise<CachedImage> {
  const bitmap = await createImageBitmap(canvas as HTMLCanvasElement);
  return buildMips(bitmap);
}

/**
 * Mesh-subdivision projective warp (IMG-5 CPU fallback-as-primary): the destination
 * rect is split into a WARP_GRID² mesh; each cell is drawn with the affine transform
 * that maps its source triangle to its destination triangle.
 */
export function warpBitmap(
  source: ImageBitmap,
  h: number[],
  rw: number,
  rh: number
): OffscreenCanvas | HTMLCanvasElement | null {
  const inv = invertHomography(h); // rectified px → source px
  if (!inv) return null;
  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(rw, rh)
      : Object.assign(document.createElement("canvas"), { width: rw, height: rh });
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D | null;
  if (!ctx) return null;

  const srcAt = (x: number, y: number): Vec => applyHomography(inv, { x, y });
  const cellW = rw / WARP_GRID;
  const cellH = rh / WARP_GRID;
  for (let gy = 0; gy < WARP_GRID; gy++) {
    for (let gx = 0; gx < WARP_GRID; gx++) {
      const dst = [
        { x: gx * cellW, y: gy * cellH },
        { x: (gx + 1) * cellW, y: gy * cellH },
        { x: (gx + 1) * cellW, y: (gy + 1) * cellH },
        { x: gx * cellW, y: (gy + 1) * cellH }
      ];
      const src = dst.map((p) => srcAt(p.x, p.y));
      drawTexturedTriangle(ctx, source, [src[0]!, src[1]!, src[2]!], [dst[0]!, dst[1]!, dst[2]!]);
      drawTexturedTriangle(ctx, source, [src[0]!, src[2]!, src[3]!], [dst[0]!, dst[2]!, dst[3]!]);
    }
  }
  return canvas;
}

/** Classic canvas textured-triangle: clip to dst tri, drawImage with the src→dst affine. */
function drawTexturedTriangle(
  ctx: CanvasRenderingContext2D,
  img: ImageBitmap,
  src: [Vec, Vec, Vec],
  dst: [Vec, Vec, Vec]
): void {
  const [s0, s1, s2] = src;
  const [d0, d1, d2] = dst;
  const denom = (s1.x - s0.x) * (s2.y - s0.y) - (s2.x - s0.x) * (s1.y - s0.y);
  if (Math.abs(denom) < 1e-12) return;
  const a = ((d1.x - d0.x) * (s2.y - s0.y) - (d2.x - d0.x) * (s1.y - s0.y)) / denom;
  const b = ((d1.y - d0.y) * (s2.y - s0.y) - (d2.y - d0.y) * (s1.y - s0.y)) / denom;
  const c = ((d2.x - d0.x) * (s1.x - s0.x) - (d1.x - d0.x) * (s2.x - s0.x)) / denom;
  const d = ((d2.y - d0.y) * (s1.x - s0.x) - (d1.y - d0.y) * (s2.x - s0.x)) / denom;
  const e = d0.x - a * s0.x - c * s0.y;
  const f = d0.y - b * s0.x - d * s0.y;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(d0.x, d0.y);
  ctx.lineTo(d1.x, d1.y);
  ctx.lineTo(d2.x, d2.y);
  ctx.closePath();
  // expand the clip a hair to hide seams between triangles
  ctx.clip();
  ctx.setTransform(a, b, c, d, e, f);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}
